import { useState } from 'react'
import {
  Button,
  Checkbox,
  Empty,
  Space,
  Spin,
  Tooltip,
  Tree,
  Typography,
} from 'antd'
import {
  FileOutlined,
  FolderOutlined,
  ReloadOutlined,
  RightOutlined,
  DownOutlined,
} from '@ant-design/icons'
import type { DriveItemTreeProps, WorkflowFileTreeNode } from './types'
import type { AntTreeNodeProps } from 'antd/es/tree'
import styles from './index.module.css'

/**
 * Displays a record folder as an Ant Design tree with lazily loaded branches
 * and an independent, labeled checkbox for each item.
 */
function DriveItemTree({
  rootId,
  branches,
  refreshing = false,
  selected,
  disabled,
  disabledReason,
  onSelect,
  onLoad,
}: DriveItemTreeProps) {
  const [expanded, setExpanded] = useState<React.Key[]>([])
  const [loading, setLoading] = useState<Set<string>>(new Set())
  const [focused, setFocused] = useState<React.Key | null>(null)
  const visible: WorkflowFileTreeNode[] = []

  /**
   * Builds tree nodes for every loaded branch while collecting only expanded,
   * visible rows for keyboard navigation.
   */
  const buildNodes = (
    folderId: string,
    level = 1,
    isVisible = true
  ): WorkflowFileTreeNode[] => {
    const items = branches[folderId]?.items ?? []
    return items.map((item, index) => {
      const node: WorkflowFileTreeNode = {
        key: item.id,
        title: item.name,
        item,
        level,
        position: index + 1,
        siblingCount: items.length,
        isLeaf: item.kind !== 'folder',
        children: [],
      }
      if (isVisible) visible.push(node)
      if (item.kind === 'folder')
        node.children = buildNodes(
          item.id,
          level + 1,
          isVisible && expanded.includes(item.id)
        )
      return node
    })
  }
  const treeData = buildNodes(rootId)
  const activeNode = visible.find((node) => node.key === focused) ?? visible[0]

  /** Toggles one item; selecting a folder never selects its descendants. */
  const select = (id: string) =>
    onSelect(
      selected.includes(id)
        ? selected.filter((value) => value !== id)
        : [...selected, id]
    )

  /**
   * Tracks a branch's loading indicator. `onLoad` owns caching and failures,
   * and resolves even when loading fails.
   */
  const load = async (id: string) => {
    setLoading((previous) => new Set(previous).add(id))
    try {
      await onLoad(id)
    } finally {
      setLoading((previous) => {
        const next = new Set(previous)
        next.delete(id)
        return next
      })
    }
  }

  const root = branches[rootId]
  if (!root || refreshing)
    return (
      <div className={styles.loading}>
        <Spin tip="Loading files…" />
      </div>
    )
  if (root.error)
    return (
      <Button onClick={() => void onLoad(rootId)} icon={<ReloadOutlined />}>
        Retry loading files
      </Button>
    )
  if (!root.items.length)
    return (
      <Empty
        description="This folder is empty"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    )

  const filesOnly = root.items.every((item) => item.kind !== 'folder')
  return (
    <Tree<WorkflowFileTreeNode>
      aria-label="Record files"
      aria-multiselectable
      className={`${styles.tree} ${filesOnly ? styles.filesOnly : ''}`}
      blockNode
      virtual={false}
      motion={null}
      selectable={false}
      showLine={{ showLeafIcon: false }}
      treeData={treeData}
      expandedKeys={expanded}
      activeKey={activeNode?.key ?? null}
      onActiveChange={setFocused}
      onExpand={(keys, { node, expanded: open }) => {
        setExpanded(keys)
        const id = node.item.id
        if (open && !loading.has(id) && (!branches[id] || branches[id].error))
          void load(id)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Home') setFocused(visible[0]?.key ?? null)
        else if (event.key === 'End')
          setFocused(visible[visible.length - 1]?.key ?? null)
        else if (
          (event.key === ' ' || event.key === 'Enter') &&
          activeNode &&
          !disabled &&
          activeNode.item.kind !== 'unsupported'
        )
          select(activeNode.item.id)
        else return
        event.preventDefault()
      }}
      switcherIcon={({ expanded: open, title }: AntTreeNodeProps) => (
        <Button
          type="text"
          size="small"
          aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
          icon={open ? <DownOutlined /> : <RightOutlined />}
        />
      )}
      titleRender={({ item, level, position, siblingCount }) => {
        const branch = branches[item.id]
        const open = expanded.includes(item.id)
        return (
          <Space
            wrap
            className={styles.row}
            role="treeitem"
            aria-label={item.name}
            aria-level={level}
            aria-posinset={position}
            aria-setsize={siblingCount}
            aria-checked={selected.includes(item.id)}
            aria-expanded={item.kind === 'folder' ? open : undefined}
          >
            <Tooltip title={disabled ? disabledReason : undefined}>
              <span>
                <Checkbox
                  aria-label={`Select ${item.name}`}
                  checked={selected.includes(item.id)}
                  disabled={disabled || item.kind === 'unsupported'}
                  onChange={() => select(item.id)}
                />
              </span>
            </Tooltip>
            {item.kind === 'folder' ? (
              <FolderOutlined aria-hidden />
            ) : (
              <FileOutlined aria-hidden />
            )}
            <span>{item.name}</span>
            {item.kind === 'unsupported' && (
              <Typography.Text type="secondary">
                Unsupported item — manage in SharePoint
              </Typography.Text>
            )}
            {open && loading.has(item.id) && (
              <Spin size="small" aria-label={`Loading ${item.name}`} />
            )}
            {open && branch?.error && (
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => void load(item.id)}
                aria-label={`Retry loading ${item.name}`}
              >
                Retry
              </Button>
            )}
            {open && branch && !branch.error && !branch.items.length && (
              <Typography.Text type="secondary">Empty folder</Typography.Text>
            )}
          </Space>
        )
      }}
    />
  )
}
export default DriveItemTree
